import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Building2, ArrowUpDown, ArrowUp, ArrowDown, Calendar } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../components/ui/table';
import { formatEuro } from '../utils/format';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESTADOS = ['Todos', 'Activo', 'Ofertado', 'Arras', 'En cartera', 'Reservado', 'Vendido', 'Descartado'];

export default function Reports() {
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [selectedEstados, setSelectedEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [stats, setStats] = useState({
    total: 0,
    valorTotal: 0,
    inversionTotal: 0,
    beneficioTotal: 0
  });
  const [totals, setTotals] = useState({
    precio_compra: 0,
    precio_venta: 0,
    itp: 0,
    gastos_totales: 0,
    beneficio: 0
  });

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    filterAndCalculateStats();
  }, [selectedEstados, allProperties]);

  useEffect(() => {
    sortProperties();
  }, [sortConfig]);

  const loadProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      const props = response.data;
      setAllProperties(props);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar informe:', error);
      toast.error('Error al cargar el informe');
      setLoading(false);
    }
  };

  const toggleEstado = (estado) => {
    if (estado === 'Todos') {
      setSelectedEstados([]);
    } else {
      setSelectedEstados(prev => {
        if (prev.includes(estado)) {
          return prev.filter(e => e !== estado);
        } else {
          return [...prev, estado];
        }
      });
    }
  };

  const filterAndCalculateStats = () => {
    let filtered = allProperties;
    if (selectedEstados.length > 0) {
      filtered = allProperties.filter(p => selectedEstados.includes(p.estado));
    }
    setProperties(filtered);
    
    const valorTotal = filtered.reduce((sum, p) => sum + (p.precio_venta || 0), 0);
    const inversionTotal = filtered.reduce((sum, p) => sum + (p.precio_compra || 0) + (p.itp || 0) + (p.gastos_totales || 0), 0);
    const beneficioTotal = filtered.reduce((sum, p) => sum + (p.beneficio || 0), 0);
    
    // Calcular MDEC (Media de Días En Cartera) - solo inmuebles con dias_en_cartera > 0
    const inmueblesConDias = filtered.filter(p => p.dias_en_cartera && p.dias_en_cartera > 0);
    const sumaDias = inmueblesConDias.reduce((sum, p) => sum + p.dias_en_cartera, 0);
    const mdec = inmueblesConDias.length > 0 ? sumaDias / inmueblesConDias.length : 0;
    
    // Calcular totales para la tabla
    setTotals({
      precio_compra: filtered.reduce((sum, p) => sum + (p.precio_compra || 0), 0),
      precio_venta: filtered.reduce((sum, p) => sum + (p.precio_venta || 0), 0),
      itp: filtered.reduce((sum, p) => sum + (p.itp || 0), 0),
      gastos_totales: filtered.reduce((sum, p) => sum + (p.gastos_totales || 0), 0),
      beneficio: filtered.reduce((sum, p) => sum + (p.beneficio || 0), 0)
    });
    
    setStats({
      total: filtered.length,
      valorTotal,
      inversionTotal,
      beneficioTotal,
      mdec
    });
  };

  const sortProperties = () => {
    if (!sortConfig.key) return;
    
    const sorted = [...properties].sort((a, b) => {
      let aVal, bVal;
      
      // Cálculo especial para porcentaje_beneficio_anual
      if (sortConfig.key === 'porcentaje_beneficio_anual') {
        aVal = a.dias_en_cartera && a.dias_en_cartera > 0 
          ? (a.porcentaje_beneficio || 0) * 365 / a.dias_en_cartera 
          : 0;
        bVal = b.dias_en_cartera && b.dias_en_cartera > 0 
          ? (b.porcentaje_beneficio || 0) * 365 / b.dias_en_cartera 
          : 0;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setProperties(sorted);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={12} className="ml-1 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={12} className="ml-1" />
      : <ArrowDown size={12} className="ml-1" />;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 0;
    return Math.round(num);
  };

  const exportToXLS = () => {
    if (properties.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    // Preparar datos con formato
    const data = properties.map(p => ({
      'ID': p.ref || '-',
      'Dirección': p.direccion,
      'Fuente': p.fuente || '-',
      'Estado': p.estado || 'Activo',
      'Precio Compra': formatNumber(p.precio_compra),
      'Precio Venta': formatNumber(p.precio_venta),
      'Precio Referencia': formatNumber(p.precio_referencia),
      'ITP': formatNumber(p.itp),
      'Gastos': formatNumber(p.gastos_totales),
      'Días Cartera': p.dias_en_cartera || 0,
      'Beneficio': formatNumber(p.beneficio),
      '% Beneficio': p.porcentaje_beneficio ? Number(p.porcentaje_beneficio.toFixed(2)) : 0,
      'Superficie': p.superficie || 0,
      'Habitaciones': p.habitaciones || 0,
      'Baños': p.banos || 0
    }));

    // Crear workbook y worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    
    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 12 },  // ID
      { wch: 35 },  // Dirección
      { wch: 15 },  // Fuente
      { wch: 12 },  // Estado
      { wch: 14 },  // Precio Compra
      { wch: 14 },  // Precio Venta
      { wch: 16 },  // Precio Referencia
      { wch: 12 },  // ITP
      { wch: 12 },  // Gastos
      { wch: 12 },  // Días Cartera
      { wch: 12 },  // Beneficio
      { wch: 12 },  // % Beneficio
      { wch: 10 },  // Superficie
      { wch: 12 },  // Habitaciones
      { wch: 8 },   // Baños
    ];
    ws['!cols'] = colWidths;

    // Formato de números con separador de miles
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      // Columnas numéricas (E a I = índices 4-8, K = 10)
      for (let C = 4; C <= 8; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[cellRef]) {
          ws[cellRef].z = '#,##0';
        }
      }
      // Beneficio (columna K = índice 10)
      const beneficioCell = XLSX.utils.encode_cell({ r: R, c: 10 });
      if (ws[beneficioCell]) {
        ws[beneficioCell].z = '#,##0';
      }
      // % Beneficio con 2 decimales (columna L = índice 11)
      const pctCell = XLSX.utils.encode_cell({ r: R, c: 11 });
      if (ws[pctCell]) {
        ws[pctCell].z = '0.00';
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Inmuebles');
    
    // Generar nombre de archivo con fecha y filtro
    const fecha = new Date().toISOString().split('T')[0];
    const filtro = selectedEstados.length > 0 ? `_${selectedEstados.join('-')}` : '';
    const filename = `informe_inmuebles${filtro}_${fecha}.xlsx`;
    
    XLSX.writeFile(wb, filename);
    toast.success('Informe exportado correctamente');
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Informes</h1>
          <p className="text-xs text-muted-foreground mt-1">Estadísticas de inmuebles</p>
        </div>
        <Button 
          onClick={exportToXLS}
          data-testid="export-xls-btn"
          size="sm"
          className="shadow-md w-full sm:w-auto"
        >
          <Download className="mr-2" size={16} />
          Exportar Excel
        </Button>
      </div>

      {/* Filtro por Estado con scroll horizontal en móvil */}
      <div className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap scrollbar-hide">
        {ESTADOS.map(estado => (
          <Button
            key={estado}
            variant={estado === 'Todos' 
              ? (selectedEstados.length === 0 ? 'default' : 'outline')
              : (selectedEstados.includes(estado) ? 'default' : 'outline')
            }
            size="sm"
            className="text-xs px-2 py-1 h-7 flex-shrink-0"
            onClick={() => toggleEstado(estado)}
            data-testid={`report-filter-${estado.toLowerCase().replace(' ', '-')}`}
          >
            {estado}
          </Button>
        ))}
      </div>

      {/* Métricas con grid responsivo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
        <Card className="px-3 md:px-4 py-2 shadow-sm">
          <p className="text-[10px] md:text-xs text-muted-foreground truncate">Inmuebles {selectedEstados.length > 0 ? selectedEstados.join(', ') : ''}</p>
          <div className="flex items-center gap-1.5">
            <Building2 className="text-primary flex-shrink-0" size={14} />
            <p className="text-sm md:text-base font-bold" data-testid="report-total">{stats.total}</p>
          </div>
        </Card>

        <Card className="px-3 md:px-4 py-2 shadow-sm">
          <p className="text-[10px] md:text-xs text-muted-foreground">Valor Total</p>
          <p className="text-sm md:text-base font-bold text-primary truncate" data-testid="report-valor">
            {formatEuro(stats.valorTotal)}
          </p>
        </Card>

        <Card className="px-3 md:px-4 py-2 shadow-sm">
          <p className="text-[10px] md:text-xs text-muted-foreground">Inversión Total</p>
          <p className="text-sm md:text-base font-bold truncate" data-testid="report-inversion">
            {formatEuro(stats.inversionTotal)}
          </p>
        </Card>

        <Card className="px-3 md:px-4 py-2 shadow-sm">
          <p className="text-[10px] md:text-xs text-muted-foreground">Beneficio Est.</p>
          <p className={`text-sm md:text-base font-bold truncate ${stats.beneficioTotal >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="report-beneficio">
            {formatEuro(stats.beneficioTotal)}
          </p>
        </Card>

        <Card className="px-3 md:px-4 py-2 shadow-sm">
          <p className="text-[10px] md:text-xs text-muted-foreground">% Bº Medio</p>
          <p className={`text-sm md:text-base font-bold ${stats.inversionTotal > 0 ? (stats.beneficioTotal / stats.inversionTotal * 100) >= 0 ? 'text-green-600' : 'text-red-600' : ''}`} data-testid="report-pct-medio">
            {stats.inversionTotal > 0 ? ((stats.beneficioTotal / stats.inversionTotal) * 100).toFixed(1) : '0.0'}%
          </p>
        </Card>

        <Card className="px-3 md:px-4 py-2 shadow-sm">
          <p className="text-[10px] md:text-xs text-muted-foreground">MDEC</p>
          <div className="flex items-center gap-1.5">
            <Calendar className="text-primary flex-shrink-0" size={14} />
            <p className="text-sm md:text-base font-bold" data-testid="report-mdec">
              {stats.mdec ? stats.mdec.toFixed(0) : '0'} días
            </p>
          </div>
        </Card>
      </div>

      <Card className="shadow-sm overflow-hidden">
        <div className="p-3 md:p-4">
          <h2 className="text-sm md:text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Inmuebles {selectedEstados.length > 0 ? `- ${selectedEstados.join(', ')}` : ''}
          </h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Cargando...</div>
        ) : properties.length > 0 ? (
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('ref')}
                >
                  <div className="flex items-center">ID <SortIcon columnKey="ref" /></div>
                </TableHead>
                <TableHead 
                  className="text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('direccion')}
                >
                  <div className="flex items-center">Dirección <SortIcon columnKey="direccion" /></div>
                </TableHead>
                <TableHead 
                  className="text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('fuente')}
                >
                  <div className="flex items-center">Fuente <SortIcon columnKey="fuente" /></div>
                </TableHead>
                <TableHead 
                  className="text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('estado')}
                >
                  <div className="flex items-center">Estado <SortIcon columnKey="estado" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('precio_compra')}
                >
                  <div className="flex items-center justify-end">Compra <SortIcon columnKey="precio_compra" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('precio_venta')}
                >
                  <div className="flex items-center justify-end">Venta <SortIcon columnKey="precio_venta" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('itp')}
                >
                  <div className="flex items-center justify-end">ITP <SortIcon columnKey="itp" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('gastos_totales')}
                >
                  <div className="flex items-center justify-end">Gastos <SortIcon columnKey="gastos_totales" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs"
                >
                  <div className="flex items-center justify-end">Inver. Total</div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('beneficio')}
                >
                  <div className="flex items-center justify-end">Beneficio <SortIcon columnKey="beneficio" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('dias_en_cartera')}
                >
                  <div className="flex items-center justify-end">Días <SortIcon columnKey="dias_en_cartera" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('porcentaje_beneficio')}
                >
                  <div className="flex items-center justify-end">% B/Bruto <SortIcon columnKey="porcentaje_beneficio" /></div>
                </TableHead>
                <TableHead 
                  className="text-right text-xs cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('porcentaje_beneficio_anual')}
                >
                  <div className="flex items-center justify-end">% B/Anual <SortIcon columnKey="porcentaje_beneficio_anual" /></div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.map((property) => (
                <TableRow key={property.id} data-testid={`report-row-${property.id}`}>
                  <TableCell className="text-xs">
                    <Link 
                      to={`/properties/${property.id}`} 
                      className="text-primary font-semibold hover:underline"
                      data-testid={`report-link-${property.id}`}
                    >
                      {property.ref || '-'}
                    </Link>
                  </TableCell>
                  <TableCell className="font-medium text-xs">{property.direccion}</TableCell>
                  <TableCell className="text-xs">{property.fuente || '-'}</TableCell>
                  <TableCell className="text-xs">{property.estado || 'Activo'}</TableCell>
                  <TableCell className="text-right text-xs">{formatEuro(property.precio_compra)}</TableCell>
                  <TableCell className="text-right text-xs">{formatEuro(property.precio_venta)}</TableCell>
                  <TableCell className="text-right text-xs">{formatEuro(property.itp)}</TableCell>
                  <TableCell className="text-right text-xs">{formatEuro(property.gastos_totales)}</TableCell>
                  <TableCell className="text-right text-xs font-medium">{formatEuro((property.precio_compra || 0) + (property.itp || 0) + (property.gastos_totales || 0))}</TableCell>
                  <TableCell className={`text-right text-xs font-semibold ${property.beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatEuro(property.beneficio)}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    {property.dias_en_cartera !== null && property.dias_en_cartera !== undefined ? property.dias_en_cartera : '-'}
                  </TableCell>
                  <TableCell className={`text-right text-xs font-semibold ${property.porcentaje_beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {property.porcentaje_beneficio ? property.porcentaje_beneficio.toFixed(1) : '0'}%
                  </TableCell>
                  <TableCell className={`text-right text-xs font-semibold ${property.porcentaje_beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {property.dias_en_cartera && property.dias_en_cartera > 0 
                      ? ((property.porcentaje_beneficio || 0) * 365 / property.dias_en_cartera).toFixed(1) + '%'
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="bg-muted/50 font-bold">
                <TableCell className="text-xs">TOTALES</TableCell>
                <TableCell className="text-xs"></TableCell>
                <TableCell className="text-xs"></TableCell>
                <TableCell className="text-xs"></TableCell>
                <TableCell className="text-right text-xs">{formatEuro(totals.precio_compra)}</TableCell>
                <TableCell className="text-right text-xs">{formatEuro(totals.precio_venta)}</TableCell>
                <TableCell className="text-right text-xs">{formatEuro(totals.itp)}</TableCell>
                <TableCell className="text-right text-xs">{formatEuro(totals.gastos_totales)}</TableCell>
                <TableCell className="text-right text-xs">{formatEuro((totals.precio_compra || 0) + (totals.itp || 0) + (totals.gastos_totales || 0))}</TableCell>
                <TableCell className={`text-right text-xs ${totals.beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatEuro(totals.beneficio)}
                </TableCell>
                <TableCell className="text-right text-xs"></TableCell>
                <TableCell className="text-right text-xs"></TableCell>
                <TableCell className="text-right text-xs"></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="mx-auto mb-3" size={32} />
            <p className="text-sm">No hay inmuebles {selectedEstados.length > 0 ? `en estado "${selectedEstados.join(', ')}"` : ''}</p>
          </div>
        )}
      </Card>
    </div>
  );
}